import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import * as LogRocket from 'logrocket';
import { PreviewTokenService } from '../portal-url.service/preview-token.service';
import { isPreviewPage, withoutPreviewHeader } from '../portal-url.service/share-link-request';

@Injectable({
  providedIn: 'root'
})
export class LogRocketService {
  private isInitialized = false;
  logRocketStarted$ = new BehaviorSubject<boolean>(false);

  // Anonymous checkout carries payment details, so its body is never recorded.
  private static readonly SENSITIVE_URL_FRAGMENTS = [
    '/JustiFi/CheckoutPayment',
  ];

  // Matched against body keys and header names only: a secret in a query string or free text still reaches
  // LogRocket. A bare `code` is the sign-in or reset code, so it matches whole; zipCode or costCode is data.
  private static readonly PII_FIELD_PATTERN =
    /(ssn|social.?security|tax_?id|identification_?number|routing_?number|account_?number|date_?of_?birth|\bdob\b|password|passwd|pwd|token|secret|authorization|cookie|^code$|^otp|(auth|one_?time|reset|verification)_?code)/i;

  constructor(private previewTokens: PreviewTokenService) { }

  initializeLogRocket(): void {
    if (isPreviewPage(window.location.hash, window.location.search, this.previewTokens.token)) {
      return;
    }

    try {
      if (!this.isInitialized && environment.logRocketAppId) {
        LogRocket.init(environment.logRocketAppId, {
          // Share-link viewers are a contractor's end customers, so their captured bodies are
          // sanitized here; `data-private` only masks the DOM, never the network layer.
          network: {
            requestSanitizer: (request) => this.sanitizeNetworkEvent(withoutPreviewHeader(request)),
            responseSanitizer: (response) => this.sanitizeNetworkEvent(response),
          },
        });
        this.isInitialized = true;
        this.logRocketStarted$.next(true);
        console.log("LogRocket session started.");
      }
    } catch (err) {
      console.error('Failed to initialize LogRocket:', err);
    }
  }

  /**
   * LogRocket captures headers and bodies verbatim and redacts nothing itself, so every layer of
   * redaction these sessions get happens here. Fails closed: a body it cannot scrub is dropped.
   */
  private sanitizeNetworkEvent(event: any): any {
    try {
      if (!event) return event;
      const url: string = event.url || '';

      // Headers are captured even on the endpoints whose body is dropped wholesale below.
      if (event.headers && typeof event.headers === 'object') {
        event.headers = this.scrubPii(event.headers);
      }

      if (LogRocketService.SENSITIVE_URL_FRAGMENTS.some(fragment => url.includes(fragment))) {
        event.body = null;
        return event;
      }

      if (event.body && (Array.isArray(event.body) || Object.getPrototypeOf(event.body) === Object.prototype)) {
        event.body = this.scrubPii(event.body);
      } else if (typeof event.body === 'string') {
        // Every string body must be parsed, not just credential-looking ones, so a
        // non-JSON body falls to the catch below and is dropped rather than recorded raw.
        event.body = JSON.stringify(this.scrubPii(JSON.parse(event.body)));
      } else if (event.body) {
        // A non-plain-object body (e.g. URLSearchParams) has no own keys scrubPii can walk,
        // so it must be dropped rather than passed through unscrubbed.
        event.body = null;
      }
    } catch {
      event.body = null;
    }
    return event;
  }

  private scrubPii(value: any): any {
    if (Array.isArray(value)) {
      return value.map(item => this.scrubPii(item));
    }
    if (value && typeof value === 'object') {
      const copy: Record<string, unknown> = {};
      for (const key of Object.keys(value)) {
        copy[key] = LogRocketService.PII_FIELD_PATTERN.test(key)
          ? '[REDACTED]'
          : this.scrubPii(value[key]);
      }
      return copy;
    }
    return value;
  }

  identify(sessionData: any): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      if (sessionData) {
        const logRocketData: any = {
          userId: sessionData.userId || '',
          email: sessionData.email || '',
          name: sessionData.displayName || '',
          phoneNumbers: sessionData.phoneNumbers_str || '',
          buildNumber: sessionData.buildNumber_str || '',
          companyName: sessionData.companyName_str || '',
          companyAddress: sessionData.companyAddress_str || '',
          portalType: 'customer',
          environment: environment.production ? 'production' : 'development'
        };

        LogRocket.identify(logRocketData.userId, logRocketData);
      }
    } catch (err) {
      console.error('Failed to identify user in LogRocket:', err);
    }
  }

  track(eventName: string, properties?: any): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      LogRocket.track(eventName, properties);
    } catch (err) {
      console.error(`Failed to track LogRocket event ${eventName}:`, err);
    }
  }

  captureMessage(message: string, extra?: any): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      LogRocket.captureMessage(message, extra);
    } catch (err) {
      console.error('Failed to capture LogRocket message:', err);
    }
  }

  getSessionURL(): string | null {
    if (!this.isInitialized) {
      return null;
    }

    try {
      return LogRocket.sessionURL;
    } catch (err) {
      console.error('Error getting LogRocket session URL:', err);
      return null;
    }
  }

  getSessionURLWithRetry(maxRetries: number = 5, delayMs: number = 1000): Promise<string | null> {
    return new Promise((resolve) => {
      let attempts = 0;

      const tryGetUrl = () => {
        attempts++;
        const url = this.getSessionURL();

        if (url) {
          resolve(url);
        } else if (attempts < maxRetries) {
          setTimeout(tryGetUrl, delayMs);
        } else {
          resolve(null);
        }
      };

      tryGetUrl();
    });
  }
}